import openResource from '@/common/serve/openResource';
import {resourceUrlConnect} from '@/common/utils/utils';
import type {
  DeviceSeries,
  DownloadItem,
  FileTypeFilter,
  MenuNode,
} from './mock';

/** 菜单树 → 公开页设备树（产品线→系列→设备） */
export function menuToDeviceTree(menu: MenuNode[]): DeviceSeries[] {
  const nodes = menu || [];
  const result: DeviceSeries[] = [];

  for (let i = 0; i < nodes.length; i++) {
    const productLine = nodes[i];
    const seriesNodes = sortMenuNodes(productLine.children || []);
    for (let j = 0; j < seriesNodes.length; j++) {
      const series = seriesNodes[j];
      const models = sortMenuNodes(series.children || []);
      result.push({
        seriesId: series.id,
        seriesName: productLine.name + ' / ' + series.name,
        devices: models.map(function (device) {
          return { id: device.id, name: device.name };
        }),
      });
    }
  }

  return result;
}

export interface SeriesBlock {
  seriesId: string;
  seriesName: string;
  devices: Array<{ id: string; name: string }>;
}

function isMenuLeaf(node: MenuNode) {
  return !(node.children && node.children.length);
}

export function sortMenuNodes(nodes: MenuNode[]): MenuNode[] {
  return (nodes || []).slice().sort(function (a, b) {
    return (a.sort || 0) - (b.sort || 0);
  });
}

/** 某产品线下的系列 + 设备（三级：产品线 → 系列 → 设备） */
export function getSeriesBlocksOfLine(line: MenuNode): SeriesBlock[] {
  const seriesList = sortMenuNodes(line.children || []);
  const result: SeriesBlock[] = [];
  for (let i = 0; i < seriesList.length; i++) {
    const series = seriesList[i];
    result.push({
      seriesId: series.id,
      seriesName: series.name,
      devices: sortMenuNodes(series.children || []).map(function (device) {
        return { id: device.id, name: device.name };
      }),
    });
  }
  return result;
}

/** 全部产品线下的系列块（系列名带产品线前缀） */
export function getAllSeriesBlocks(menu: MenuNode[]): SeriesBlock[] {
  const lines = sortMenuNodes(menu);
  const result: SeriesBlock[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const blocks = getSeriesBlocksOfLine(line);
    for (let j = 0; j < blocks.length; j++) {
      const block = blocks[j];
      result.push({
        seriesId: block.seriesId,
        seriesName: line.name + ' / ' + block.seriesName,
        devices: block.devices,
      });
    }
  }
  return result;
}

function menuContainsId(node: MenuNode, id: string): boolean {
  if (node.id === id) {
    return true;
  }
  const children = node.children || [];
  for (let i = 0; i < children.length; i++) {
    if (menuContainsId(children[i], id)) {
      return true;
    }
  }
  return false;
}

/** 根据已选设备定位所属产品线 */
export function findProductLineIdByDevice(menu: MenuNode[], deviceId: string): string {
  if (!deviceId || deviceId === 'all') {
    return 'all';
  }
  const lines = menu || [];
  for (let i = 0; i < lines.length; i++) {
    if (menuContainsId(lines[i], deviceId)) {
      return lines[i].id;
    }
  }
  return 'all';
}

/** 叶子设备 id → 名称 */
export function buildMenuDeviceNameMap(menu: MenuNode[]): Record<string, string> {
  const map: Record<string, string> = {};
  function walk(nodes: MenuNode[]) {
    const list = nodes || [];
    for (let i = 0; i < list.length; i++) {
      const node = list[i];
      if (isMenuLeaf(node)) {
        map[node.id] = node.name;
      } else {
        walk(node.children || []);
      }
    }
  }
  walk(menu);
  return map;
}

/** 设备 id → 名称（用于列表展示） */
export function buildDeviceNameMap(tree: DeviceSeries[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (let i = 0; i < tree.length; i++) {
    const devices = tree[i].devices || [];
    for (let j = 0; j < devices.length; j++) {
      map[devices[j].id] = devices[j].name;
    }
  }
  return map;
}

/** 相对路径拼完整下载地址 */
export function resolveDownloadUrl(fileUrl: string): string {
  if (!fileUrl) {
    return '';
  }
  if (fileUrl.indexOf('http') === 0) {
    return fileUrl;
  }
  return resourceUrlConnect(fileUrl);
}

export function buildPublicListParams(opts: {
  deviceSeriesId: string;
  fileType: FileTypeFilter;
  keyword: string;
  page: number;
  pageSize: number;
}): Record<string, any> {
  const params: Record<string, any> = {
    pIdx: opts.page > 0 ? opts.page : 1,
    pSize: opts.pageSize > 0 ? opts.pageSize : 10,
    enabled: true,
  };
  if (opts.deviceSeriesId && opts.deviceSeriesId !== 'all') {
    params.deviceSeriesId = opts.deviceSeriesId;
  }
  if (opts.fileType && opts.fileType !== 'all') {
    params.fileType = opts.fileType;
  }
  if (opts.keyword && opts.keyword.trim()) {
    params.keyword = opts.keyword.trim();
  }
  return params;
}

/** 公开页菜单树（保留接口原始多层结构） */
export async function fetchPublicMenuTree(): Promise<MenuNode[]> {
  const {success} = await openResource.getMenu();
  if (!success || !success.menu) {
    return [];
  }
  return success.menu;
}

/** 设备树（产品线→系列→设备） */
export async function fetchPublicDeviceTree(): Promise<DeviceSeries[]> {
  const menu = await fetchPublicMenuTree();
  return menuToDeviceTree(menu);
}

export async function fetchPublicDownloadList(opts: {
  deviceSeriesId: string;
  fileType: FileTypeFilter;
  keyword: string;
  page: number;
  pageSize: number;
}): Promise<{ total: number; list: DownloadItem[] }> {
  const {success} = await openResource.getFileList(buildPublicListParams(opts));
  if (!success) {
    return { total: 0, list: [] };
  }
  return {
    total: success.total || 0,
    list: success.rows || [],
  };
}
